import { createSelector } from '@reduxjs/toolkit';
import { EDIT_PERMISSIONS_OF_PROJECT } from 'scenes/Permissions/constants/permissions';
import { returnDefaultPermissionsByEmploymentType } from 'scenes/Permissions/utils/returnDefaultPermissionsByEmploymentType';
import {
  selectProjects,
  selectProjectsArray,
  selectProjectsFolder,
  selectSelectedProject,
  selectSelectedProjectId,
} from 'store/projects/selectors/projectsSelectors';
import {
  selectIsUserProjectResourcesLoaded,
  selectLoggedInUserResourcesArray,
  selectSelectedProjectCurrentUserResources,
} from 'store/resources/selectors/resources.selectors';
import {
  selectLoggedInUser,
  selectUserActiveEmployerBusinessId,
} from 'store/user/selectors.js/user.selectors';

import { uniqBy } from 'lodash-es';
import { BUSINESS_EXPLICIT_PERMISSIONS } from 'scenes/Permissions/constants/business-explicit-permissions';
import { PROJECT_EXPLICIT_PERMISSIONS } from 'scenes/Permissions/constants/project-explicit-permissions';
import { isGatekeeperClosed } from 'scenes/Permissions/utils/returnAccessPermissionByFeature.util';
import { returnTogglableDefaultBusinessPermissions } from 'scenes/Permissions/utils/returnTogglableDefaultBusinessPermissions.util';
import { selectUserProfileEmployerBusinessIds } from 'store/userProfile/selectors/user-profile.selectors';
import {
  selectUserAdminBusinessIds,
  selectUserAdminPermissions,
} from './base-project-permissions.selectors';

const selectProjectPermissionsState = (state) => state.projectPermissions;

export const selectUserProjectPermissions = createSelector(
  [selectProjectPermissionsState],
  (state) => state.userProjectPermissionsByProjectId
);

export const selectUserBusinessPermissions = createSelector(
  [selectProjectPermissionsState],
  (state) => state.userBusinessPermissions
);

export const selectIsBusinessAdminOrOwnerOfSelectedProject = createSelector(
  [selectUserAdminPermissions, selectLoggedInUser, selectSelectedProject],
  (adminPermissions, loggedInUser, project) => {
    if (!project.business_id) {
      // in case of personal mylixani project, check if project created by current user
      return project.mirrorUser === loggedInUser.id;
    } else {
      // in business projects, check for admin status
      return adminPermissions[project.business_id] ? true : false;
    }
  }
);

export const selectResourceDefaultPermissions = createSelector(
  [selectSelectedProjectCurrentUserResources],
  (selectedProjectUserResources) => {
    const resourceDefaultPermissions = returnDefaultPermissionsByEmploymentType(
      selectedProjectUserResources
    );
    return resourceDefaultPermissions;
  }
);

export const selectIsCurrentUserEmployeeInProject = createSelector(
  [selectSelectedProjectCurrentUserResources],
  (currentUserResources) => {
    const hasEmployeeResource = currentUserResources.filter(
      (resource) =>
        !resource.employmentType || resource.employmentType === 'EMPLOYEE'
    )[0];

    return hasEmployeeResource ? true : false;
  }
);

export const getNearestUserProjectPermissions = (
  projects,
  userPermissions,
  selectedProjectId
) => {
  //If permissions are not set in current project, they are inherited from nearest parent project. Parent projects are looped until permissions are found.
  const selectedProjectPermissions = userPermissions[selectedProjectId];

  const selectedProject = projects[selectedProjectId];

  const getParentProjectPermissions = (projectId) => {
    const currentProject = projects[projectId];

    const parentProjectPermissions =
      currentProject && currentProject.parent
        ? userPermissions[currentProject.parent]
        : {};

    return parentProjectPermissions
      ? parentProjectPermissions
      : // if not found, call itself again, this time with the parent of current project in loop
        getParentProjectPermissions(currentProject.parent);
  };

  if (selectedProjectPermissions) {
    return selectedProjectPermissions;
  } else if (selectedProject && selectedProject.parent) {
    return getParentProjectPermissions(selectedProjectId);
  } else {
    return {};
  }
};

export const selectSelectedFolderUserPermissions = createSelector(
  [
    selectProjectsFolder,
    selectUserProjectPermissions,
    selectProjects,
    selectResourceDefaultPermissions,
  ],
  (
    selectedFolder,
    userProjectPermissions,
    projects,
    resourceDefaultPermissions
  ) => {
    const definedUserPermissions = getNearestUserProjectPermissions(
      projects,
      userProjectPermissions,
      selectedFolder.projectId
    );

    const resourcePermissionsWithDefinedPermissions = {
      ...resourceDefaultPermissions,
      ...definedUserPermissions,
    };

    return resourcePermissionsWithDefinedPermissions;
  }
);
export const selectSelectedProjectUserPermissions = createSelector(
  [
    selectSelectedProjectId,
    selectUserProjectPermissions,
    selectProjects,
    selectResourceDefaultPermissions,
  ],
  (
    selectedProjectId,
    userProjectPermissions,
    projects,
    resourceDefaultPermissions
  ) => {
    const definedUserPermissions = getNearestUserProjectPermissions(
      projects,
      userProjectPermissions,
      selectedProjectId
    );

    const effectivePermissions = {
      ...resourceDefaultPermissions,
      ...definedUserPermissions,
    };

    return effectivePermissions;
  }
);

export const selectCurrentUserBusinessPermissions = createSelector(
  [selectUserBusinessPermissions, selectProjects],
  (userBusinessPermissions) => {
    const defaultBusinessPermissions =
      returnTogglableDefaultBusinessPermissions();

    const effectivePermissions = {
      ...defaultBusinessPermissions,
      ...userBusinessPermissions,
    };

    return effectivePermissions;
  }
);

export const selectCanAccessPermissionsManagement = createSelector(
  [
    selectSelectedProjectUserPermissions,
    selectIsBusinessAdminOrOwnerOfSelectedProject,
  ],
  (userPermissions, isOwner) =>
    isOwner || userPermissions[EDIT_PERMISSIONS_OF_PROJECT] === true
);

export const selectSelectedProjectUserPermissionsArray = createSelector(
  [selectSelectedProjectUserPermissions],
  (permissions) => (permissions ? Object.keys(permissions) : [])
);

export const selectIsPersonalPermissionsLoaded = createSelector(
  [selectProjectPermissionsState],
  (state) => state.isPersonalPermissionsLoaded
);

export const selectIsPermissionsDataLoaded = createSelector(
  [selectIsPersonalPermissionsLoaded, selectIsUserProjectResourcesLoaded],
  (isPermissionsLoaded, isResourcesLoaded) =>
    isPermissionsLoaded && isResourcesLoaded
);

export const selectIsAdminInSelectedProject = createSelector(
  [selectSelectedProject, selectUserAdminPermissions],
  (selectedProject, userAdminPermissions) =>
    userAdminPermissions[selectedProject.business_id] ? true : false
);

// Multiple use permission selector, in case of not owner, the permission's true value is checked from user permissions.
// Gatekeeper rule: every sub-permission is gated by its feature's access perm
// (see returnAccessPermissionByFeature.util). If the gatekeeper is off, this
// returns false even when the raw sub-perm is true.
export const selectIsRequiredPermission = createSelector(
  [
    selectIsBusinessAdminOrOwnerOfSelectedProject,
    selectSelectedProjectUserPermissions,
    (_, args) => args,
  ],
  (isOwner, permissions, args) => {
    if (typeof args !== 'string' || !args) return false;

    const isExplicit = PROJECT_EXPLICIT_PERMISSIONS.includes(args);

    if (isOwner && !isExplicit) return true;

    if (isGatekeeperClosed(args, permissions)) return false;

    return permissions?.[args] === true;
  }
);

export const selectIsRequiredBusinessPermissionByBusinessId = createSelector(
  [
    selectUserAdminBusinessIds,
    selectCurrentUserBusinessPermissions,
    (_, args) => args,
  ],
  (userAdminBusinessIds, businessPermissions, permissionArgs) => {
    const { businessId, permission } = permissionArgs;

    const thisBusinessPermissions = businessPermissions[businessId];
    const isExplicit = BUSINESS_EXPLICIT_PERMISSIONS.includes(permission);
    const isAdmin = userAdminBusinessIds.includes(businessId);

    if (isAdmin && !isExplicit) return true;

    // Business permissions are flat — no master/sub gating concept applies
    // here (unlike project permissions). Check the raw stored value.
    return !!(thisBusinessPermissions && thisBusinessPermissions[permission]);
  }
);

export const selectIsRequiredBusinessPermissionFromCurrentActiveEmployerBusiness =
  createSelector(
    [
      selectUserAdminBusinessIds,
      selectCurrentUserBusinessPermissions,
      selectUserActiveEmployerBusinessId,
      (_, args) => args,
    ],
    (
      userAdminBusinessIds,
      businessPermissions,
      activeEmployerBusinessId,
      permission
    ) => {
      const thisBusinessPermissions =
        businessPermissions[activeEmployerBusinessId];
      const isAdmin = userAdminBusinessIds.includes(activeEmployerBusinessId);
      const isExplicit = BUSINESS_EXPLICIT_PERMISSIONS.includes(permission);

      if (isAdmin && !isExplicit) return true;

      // Business permissions are flat — no master/sub gating concept applies.
      return !!(thisBusinessPermissions && thisBusinessPermissions[permission]);
    }
  );

export const selectEmployersWithPermissionToAdjustUserEmployerSettings =
  createSelector(
    [selectProjectsArray, selectUserProfileEmployerBusinessIds],
    (projects, adminBusinessIds) => {
      const userAdminEmployerProjects = projects.filter(
        (project) =>
          project.businessTypeEnum === 'EMPLOYER' &&
          adminBusinessIds.includes(project.business_id)
      );

      const uniqueBusinessIdEmployers = uniqBy(
        userAdminEmployerProjects,
        'business_id'
      );

      return uniqueBusinessIdEmployers;
    }
  );

// selectIsRequiredPermission, resolved against the project id passed in rather
// than the globally selected one. Use this whenever the thing being gated
// belongs to a project that need not be the selected one, so the check matches
// the one the API will run on the request.
export const selectIsRequiredPermissionForProject = createSelector(
  [
    selectUserAdminPermissions,
    selectLoggedInUser,
    selectUserProjectPermissions,
    selectProjects,
    selectLoggedInUserResourcesArray,
    (_, args) => args,
  ],
  (
    adminPermissions,
    loggedInUser,
    userProjectPermissions,
    projects,
    loggedInUserResources,
    args
  ) => {
    const { projectId, permission } = args || {};

    if (typeof permission !== 'string' || !permission) return false;

    const project = projects[projectId];

    if (!project) return false;

    const isOwner = project.business_id
      ? Boolean(adminPermissions[project.business_id])
      : // Personal myLixani project — its creator is its owner, which is what
        // the API checks too (isOwnerOfProject). Both sides can be undefined,
        // and that must not read as a match.
        Boolean(project.mirrorUser) && project.mirrorUser === loggedInUser?.id;

    const isExplicit = PROJECT_EXPLICIT_PERMISSIONS.includes(permission);

    // The owner blanket must come after the explicit check, never before it: an
    // explicit permission is precisely the kind an owner does not get for free.
    if (isOwner && !isExplicit) return true;

    const projectResources = loggedInUserResources
      ? loggedInUserResources.filter(
          (resource) => resource.project === projectId
        )
      : [];

    const effectivePermissions = {
      ...returnDefaultPermissionsByEmploymentType(projectResources),
      ...getNearestUserProjectPermissions(
        projects,
        userProjectPermissions,
        projectId
      ),
    };

    if (isGatekeeperClosed(permission, effectivePermissions)) return false;

    return effectivePermissions[permission] === true;
  }
);

export const selectIsSystemAdmin = createSelector(
  [selectProjectPermissionsState],
  (state) => state.isSystemAdmin
);

/**
 * Whether the Nerobotti launcher (and the panel behind it) should show.
 *
 * The rule is: Lixani system admins, or a `nerobotti_access` grant on a
 * project of a business with an active Nerobotti licence. The API answers the
 * licence-and-grant half in one boolean (`mayUseNerobotti` on the
 * all-permissions payload, computed by `nerobotti-door` in 3-API — the same
 * door the agent worker applies before spending a token), so the client reads
 * it rather than re-deriving a policy it would drift from. The client cannot
 * see the licence list anyway; that endpoint is system-admin-only.
 *
 * System admins are stated here as well as in the API, so the launcher keeps
 * working for them against an API build that predates the field. Hiding the
 * launcher is UX, not enforcement: the worker refuses the turn on its own.
 */
export const selectMayUseNerobotti = createSelector(
  [selectProjectPermissionsState],
  (state) => state.isSystemAdmin === true || state.mayUseNerobotti === true
);

export const selectIsPermissionToEditShadowProject = createSelector(
  [
    selectSelectedProject,
    selectUserActiveEmployerBusinessId,
    selectUserAdminBusinessIds,
  ],
  (project, activeEmployerBusinessId, userAdminBusinessIds) => {
    if (!project?.business_id || !activeEmployerBusinessId) return false;
    const isShadowProject = project.business_id !== activeEmployerBusinessId;
    if (!isShadowProject) return false;
    return userAdminBusinessIds.includes(activeEmployerBusinessId);
  }
);
